import {
  BlueprintAddValidationProblemStep,
  BlueprintOperationType,
  BlueprintParameterValueConstant,
  BlueprintParameterValueType,
  BlueprintStepTemplate,
} from "../../../models/Blueprints";
import { VALIDATION_EXIT_CODE_OPTIONS } from "../../../constants";
import useBlueprintContext from "../context/useBlueprintContext";
import DropdownFormField from "./DropdownFormField";
import TypeaheadFormField from "./TypeaheadFormField";

type ValidationProblemTypes = Record<
  string,
  {
    problem_type: string;
    title: string;
  }
>;
interface Props {
  stepTemplate: BlueprintStepTemplate;
}

const BlueprintEditorRightPanelAddValidationProblemForm = ({ stepTemplate }: Props) => {
  const {
    selectedStep,
    updateStepParameterValue,
    blueprint: { operation_type: operationType },
  } = useBlueprintContext();
  const step = selectedStep as BlueprintAddValidationProblemStep;
  const problemTypes = stepTemplate.metadata?.validation_problem_types as ValidationProblemTypes;
  const problemTypeChoices = Object.values(problemTypes ?? {}).map((choice) => ({
    id: choice.problem_type,
    name: choice.title,
  }));

  return (
    <>
      <DropdownFormField
        currentValue={
          (step.parameter_values.problem_type as BlueprintParameterValueConstant)?.constant
        }
        choices={problemTypeChoices}
        onChange={(e) =>
          updateStepParameterValue(step, "problem_type", {
            constant: e.target.value,
            value_type: BlueprintParameterValueType.constant,
          })
        }
        key={"problem_type"}
        title={"problem_type"}
        placeholder={""}
        subtitle={"The short description of the problem type."}
      />
      <DropdownFormField
        currentValue={(step.parameter_values.severity as BlueprintParameterValueConstant)?.constant}
        choices={[
          { id: "WARNING", name: "Warning" },
          { id: "ERROR", name: "Error" },
        ]}
        onChange={(e) =>
          updateStepParameterValue(step, "severity", {
            constant: e.target.value,
            value_type: BlueprintParameterValueType.constant,
          })
        }
        placeholder={""}
        key={"severity"}
        title={"severity"}
        subtitle={
          "The severity of the problem. An error will be included in the 'errors' array in the response body of the request, and will halt the request. A warning will be included in the 'warnings' array in the response body of the request, but will not halt the request."
        }
      />
      <TypeaheadFormField
        key={"detail"}
        title={"detail"}
        subtitle={"A longer description of the specific error."}
        valueKey={"detail"}
        parameterType={"string"}
      />
      <TypeaheadFormField
        title={"Field Location"}
        subtitle={"If applicable, the location of the field that this problem applies to."}
        includeUniqueIdentifierCheck={false}
        valueKey={"field_location"}
        parameterType={"field_reference"}
      />
      <TypeaheadFormField
        key={"raw_error"}
        title={"Raw Error"}
        subtitle={"The raw error message from the source API."}
        valueKey={"raw_error"}
        parameterType={"string"}
      />
      <TypeaheadFormField
        key={"error_code"}
        title={"Error Code"}
        subtitle={
          "The HTTP status code to return in the raw_api_response (e.g., 400, 429, 500). Used primarily for bootstrap accounts."
        }
        valueKey={"error_code"}
        parameterType={"integer"}
      />
      <TypeaheadFormField
        key={"required_permission"}
        title={"Required Permission"}
        subtitle={
          "The specific permission scope required to perform the operation (e.g., 'Sites.FullControl.All')."
        }
        valueKey={"required_permission"}
        parameterType={"string"}
      />
      <DropdownFormField
        currentValue={
          (step.parameter_values.exit_code as BlueprintParameterValueConstant)?.constant
        }
        choices={VALIDATION_EXIT_CODE_OPTIONS}
        onChange={(e) =>
          updateStepParameterValue(step, "exit_code", {
            constant: parseInt(e.target.value, 10),
            value_type: BlueprintParameterValueType.constant,
          })
        }
        placeholder={"Select exit code"}
        key={"exit_code"}
        title={"Exit Code"}
        subtitle={
          "The Blueprint status code that will be returned when this validation problem occurs."
        }
      />
      {operationType == BlueprintOperationType.VALIDATE && (
        <DropdownFormField
          currentValue={
            (step.parameter_values.block_merge_link as BlueprintParameterValueConstant)?.constant
          }
          choices={[
            { id: "True", name: "True" },
            { id: "False", name: "False" },
          ]}
          onChange={(e) =>
            updateStepParameterValue(step, "block_merge_link", {
              constant: e.target.value,
              value_type: BlueprintParameterValueType.constant,
            })
          }
          placeholder={""}
          key={"block_merge_link"}
          title={"Blocks Merge Link"}
          subtitle={
            "Indicates whether this problem should block the Linking flow (only applies for Validate BPs).  Linking flow errors currently default to FIX_INPUT, with the exception of Missing Permissions."
          }
        />
      )}
    </>
  );
};

export default BlueprintEditorRightPanelAddValidationProblemForm;
